import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Trash2, Loader2, Upload, X, GripVertical, Pencil, Truck, Store as StoreIcon, Eye, EyeOff } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", price: "", sizes: "", colors: "", fulfillment: "pickup", ship_fee: "", track_inventory: false, stock: "", personalization: false, personalization_fee: "5", personalization_label: "Name / text to print", images: [], store: "", size_upcharges: {} };
const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const fromList = (a) => (a || []).join(", ");

const PHOTO_SLOTS = [
  { key: "front", label: "FRONT", hint: "main photo — required" },
  { key: "back", label: "BACK", hint: "optional" },
  { key: "measurements", label: "MEASUREMENTS", hint: "optional" },
];
const photosFrom = (images, labels) => {
  const p = { front: "", back: "", measurements: "" };
  (images || []).forEach((url, i) => {
    const key = labels?.[i] || PHOTO_SLOTS[i]?.key;
    if (key && key in p && !p[key]) p[key] = url;
  });
  return p;
};

const ProductForm = ({ initial, groups, onSubmit, onCancel, submitting }) => {
  const [f, setF] = useState(initial);
  const [photos, setPhotos] = useState(() => photosFrom(initial.images, initial.image_labels));
  const [upcharges, setUpcharges] = useState(initial.size_upcharges || {});
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const slotRef = useRef("");
  const fileRef = useRef(null);
  const isEdit = !!initial.id;

  const openPicker = (key) => { slotRef.current = key; fileRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    const slot = slotRef.current;
    if (!file || !slot) return;
    if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} is over 10MB`); return; }
    setUploadingSlot(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((prev) => ({ ...prev, [slot]: data.url }));
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploadingSlot(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("Product name is required."); return; }
    if (f.price === "" || isNaN(Number(f.price))) { toast.error("Enter a valid price."); return; }
    const image_labels = PHOTO_SLOTS.map((s) => s.key).filter((k) => photos[k]);
    const images = image_labels.map((k) => photos[k]);
    if (!images.length) { toast.error("Please add the front photo."); return; }
    const size_upcharges = {};
    for (const s of toList(f.sizes)) {
      const v = parseFloat(upcharges[s]);
      if (!isNaN(v) && v > 0) size_upcharges[s] = v;
    }
    onSubmit({ ...f, images, image_labels, size_upcharges });
  };

  return (
    <form onSubmit={submit} data-testid="store-product-form" className="bg-white border border-line p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-display text-lg tracking-[0.04em]">{isEdit ? "Edit product" : "Add new product"}</h4>
        <button type="button" onClick={onCancel} className="text-navy/50 hover:text-navy"><X size={18} /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">PRODUCT NAME</label>
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="store-name-input"
            className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy" placeholder="e.g. Cheer Team Tee" />
        </div>
        <div>
          <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">PRICE (USD)</label>
          <div className="flex items-center border border-line focus-within:border-navy">
            <span className="pl-4 pr-1 text-navy/50 text-sm">$</span>
            <input value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="store-price-input" inputMode="decimal"
              className="w-full py-3 pr-4 text-sm outline-none" placeholder="22.00" />
          </div>
        </div>
        <div>
          <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">SPIRIT STORE / GROUP</label>
          <select value={f.store} onChange={(e) => setF({ ...f, store: e.target.value })} data-testid="store-group-select"
            className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy bg-white">
            <option value="">General (no group)</option>
            {(groups || []).map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
          <p className="text-navy/40 text-[11px] mt-1">Which store tab this product appears under.</p>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">DESCRIPTION</label>
          <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="store-desc-input" rows={2}
            className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy resize-none" placeholder="Short description shown on the store" />
        </div>
        <div>
          <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">SIZES (comma separated)</label>
          <input value={f.sizes} onChange={(e) => setF({ ...f, sizes: e.target.value })} data-testid="store-sizes-input"
            className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy" placeholder="YS, YM, S, M, L, XL" />
          <p className="text-navy/40 text-[11px] mt-1">Leave blank if there are no sizes.</p>
        </div>
        {toList(f.sizes).length > 0 && (
          <div>
            <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">EXTRA COST BY SIZE <span className="text-navy/40 normal-case tracking-normal">(optional — e.g. +$3 for 3XL)</span></label>
            <div className="flex flex-wrap gap-2" data-testid="size-upcharges">
              {toList(f.sizes).map((s) => (
                <div key={s} className="flex items-center border border-line focus-within:border-navy bg-white">
                  <span className="pl-2.5 pr-1.5 text-xs font-semibold text-navy/70">{s}</span>
                  <span className="text-navy/40 text-xs">+$</span>
                  <input value={upcharges[s] ?? ""} onChange={(e) => setUpcharges({ ...upcharges, [s]: e.target.value })} data-testid={`upcharge-${s.replace(/\s+/g, "-")}`} inputMode="decimal" placeholder="0"
                    className="w-14 py-2 pr-2 text-sm outline-none" />
                </div>
              ))}
            </div>
            <p className="text-navy/40 text-[11px] mt-1">Added to the price automatically when a customer picks that size. Leave at 0 for no extra charge.</p>
          </div>
        )}
        <div>
          <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">COLORS (comma separated)</label>
          <input value={f.colors} onChange={(e) => setF({ ...f, colors: e.target.value })} data-testid="store-colors-input"
            className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy" placeholder="Black, White, Red" />
          <p className="text-navy/40 text-[11px] mt-1">Leave blank if there are no colors.</p>
        </div>
        <div>
          <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">FULFILLMENT</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setF({ ...f, fulfillment: "pickup" })} data-testid="fulfillment-pickup"
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs border transition-colors ${f.fulfillment === "pickup" ? "bg-ink text-white border-ink" : "border-line text-navy/70"}`}>
              <StoreIcon size={13} /> Pickup
            </button>
            <button type="button" onClick={() => setF({ ...f, fulfillment: "ship" })} data-testid="fulfillment-ship"
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs border transition-colors ${f.fulfillment === "ship" ? "bg-ink text-white border-ink" : "border-line text-navy/70"}`}>
              <Truck size={13} /> Ship
            </button>
          </div>
        </div>
        {f.fulfillment === "ship" && (
          <div>
            <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">SHIPPING FEE (USD)</label>
            <div className="flex items-center border border-line focus-within:border-navy">
              <span className="pl-4 pr-1 text-navy/50 text-sm">$</span>
              <input value={f.ship_fee} onChange={(e) => setF({ ...f, ship_fee: e.target.value })} data-testid="store-shipfee-input" inputMode="decimal"
                className="w-full py-3 pr-4 text-sm outline-none" placeholder="6.00" />
            </div>
          </div>
        )}
        <div className="sm:col-span-2 border border-line p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-sm text-ink">Track inventory</p>
              <p className="text-navy/50 text-xs mt-0.5">Count stock and auto show "Sold Out" when it hits zero.</p>
            </div>
            <button type="button" role="switch" aria-checked={f.track_inventory} onClick={() => setF({ ...f, track_inventory: !f.track_inventory })} data-testid="track-inventory-toggle"
              className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${f.track_inventory ? "bg-gold" : "bg-navy/25"}`}>
              <span className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${f.track_inventory ? "translate-x-6" : "translate-x-0"}`} />
            </button>
          </div>
          {f.track_inventory && (
            <div className="mt-4">
              <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">QUANTITY IN STOCK</label>
              <input value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value.replace(/[^0-9]/g, "") })} data-testid="store-stock-input" inputMode="numeric"
                className="w-40 border border-line px-4 py-3 text-sm outline-none focus:border-navy" placeholder="40" />
            </div>
          )}
        </div>
        <div className="sm:col-span-2 border border-line p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-sm text-ink">Allow personalization</p>
              <p className="text-navy/50 text-xs mt-0.5">Let customers add a name/text for an extra fee.</p>
            </div>
            <button type="button" role="switch" aria-checked={f.personalization} onClick={() => setF({ ...f, personalization: !f.personalization })} data-testid="personalization-toggle"
              className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${f.personalization ? "bg-gold" : "bg-navy/25"}`}>
              <span className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${f.personalization ? "translate-x-6" : "translate-x-0"}`} />
            </button>
          </div>
          {f.personalization && (
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">EXTRA FEE (USD)</label>
                <div className="flex items-center border border-line focus-within:border-navy">
                  <span className="pl-4 pr-1 text-navy/50 text-sm">$</span>
                  <input value={f.personalization_fee} onChange={(e) => setF({ ...f, personalization_fee: e.target.value })} data-testid="personalization-fee-input" inputMode="decimal"
                    className="w-full py-3 pr-4 text-sm outline-none" placeholder="5.00" />
                </div>
              </div>
              <div>
                <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">PROMPT SHOWN TO CUSTOMER</label>
                <input value={f.personalization_label} onChange={(e) => setF({ ...f, personalization_label: e.target.value })} data-testid="personalization-label-input"
                  className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy" placeholder="Name / text to print" />
              </div>
            </div>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">PHOTOS <span className="text-navy/40 normal-case tracking-normal">(shown to customers in this order — front is the main photo)</span></label>
          <div className="grid grid-cols-3 gap-3 max-w-xl" data-testid="store-photo-slots">
            {PHOTO_SLOTS.map((slot) => (
              <div key={slot.key}>
                <p className="text-[10px] tracking-[0.14em] text-navy/60 font-semibold mb-1.5">{slot.label} <span className="text-navy/35 normal-case tracking-normal">({slot.hint})</span></p>
                {photos[slot.key] ? (
                  <div className="relative w-full aspect-square border border-line bg-[#e9e6dd]">
                    <img src={resolveImg(photos[slot.key])} alt={`${slot.label} photo`} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setPhotos((p) => ({ ...p, [slot.key]: "" }))} data-testid={`store-photo-remove-${slot.key}`}
                      className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-700"><X size={11} /></button>
                  </div>
                ) : (
                  <button type="button" onClick={() => openPicker(slot.key)} data-testid={`store-photo-add-${slot.key}`}
                    className="w-full aspect-square border border-dashed border-line hover:border-navy/50 flex flex-col items-center justify-center gap-1.5 text-navy/50 hover:text-navy transition-colors">
                    {uploadingSlot === slot.key
                      ? <Loader2 size={16} className="animate-spin" />
                      : <><Plus size={16} /><span className="text-[10px] tracking-[0.1em] font-semibold">UPLOAD</span></>}
                  </button>
                )}
              </div>
            ))}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} data-testid="store-file-input" className="hidden" />
        </div>
      </div>
      <button type="submit" disabled={submitting} data-testid="store-submit-button"
        className="mt-5 flex items-center gap-2 bg-gold text-ink px-6 py-3 text-xs font-bold tracking-[0.14em] hover:bg-gold-soft transition-colors disabled:opacity-60">
        {submitting ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
        {submitting ? "SAVING…" : "SAVE PRODUCT"}
      </button>
    </form>
  );
};

export const StoreManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState("");
  const [filter, setFilter] = useState("ALL");

  const loadGroups = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/store-groups");
      setGroups(data);
    } catch {
      setGroups([]);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const addGroup = async () => {
    const name = newGroup.trim();
    if (!name) return;
    try {
      await api.post("/admin/store-groups", { name });
      setNewGroup("");
      loadGroups();
      toast.success(`Store "${name}" added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add the store.");
    }
  };

  const renameGroup = async (g) => {
    const name = window.prompt("Rename store (products move with it):", g.name);
    if (!name || !name.trim() || name.trim() === g.name) return;
    try {
      await api.put(`/admin/store-groups/${g.id}`, { name: name.trim() });
      loadGroups();
      load();
      toast.success("Store renamed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't rename.");
    }
  };

  const toggleGroup = async (g) => {
    try {
      await api.put(`/admin/store-groups/${g.id}`, { active: g.active === false });
      loadGroups();
      toast.success(g.active === false ? `"${g.name}" is now OPEN to the public` : `"${g.name}" is now hidden from the public`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update the store.");
    }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete the "${g.name}" store tab?`)) return;
    try {
      await api.delete(`/admin/store-groups/${g.id}`);
      loadGroups();
      toast.success("Store removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete.");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/store/products/all");
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const buildPayload = (f) => ({
    name: f.name.trim(),
    description: f.description.trim(),
    img: f.images[0],
    images: f.images,
    image_labels: f.image_labels || [],
    store: f.store || "",
    size_upcharges: f.size_upcharges || {},
    price: Number(f.price),
    sizes: toList(f.sizes),
    colors: toList(f.colors),
    fulfillment: f.fulfillment,
    ship_fee: f.fulfillment === "ship" ? Number(f.ship_fee || 0) : 0,
    track_inventory: !!f.track_inventory,
    stock: f.track_inventory ? Number(f.stock || 0) : 0,
    personalization: !!f.personalization,
    personalization_fee: f.personalization ? Number(f.personalization_fee || 0) : 0,
    personalization_label: f.personalization ? (f.personalization_label || "Name / text to print") : "Name / text to print",
  });

  const create = async (f) => {
    setSubmitting(true);
    try {
      await api.post("/admin/store", buildPayload(f));
      toast.success("Product added");
      setAdding(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add product.");
    } finally {
      setSubmitting(false);
    }
  };

  const update = async (id, f) => {
    setSubmitting(true);
    try {
      await api.put(`/admin/store/${id}`, buildPayload(f));
      toast.success("Product updated");
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update product.");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this product from the store?")) return;
    try {
      await api.delete(`/admin/store/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Product removed");
    } catch {
      toast.error("Failed to remove.");
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.put(`/admin/store/${item.id}`, { active: !item.active });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, active: !i.active } : i)));
      toast.success(!item.active ? "Product is now visible" : "Product hidden from store");
    } catch {
      toast.error("Failed to update.");
    }
  };

  const persistOrder = async (newItems) => {
    setItems(newItems);
    try {
      await api.put("/admin/store-reorder", { ordered_ids: newItems.map((i) => i.id) });
    } catch {
      toast.error("Couldn't save the new order.");
      load();
    }
  };

  const handleDrop = (dropIndex) => {
    if (filter !== "ALL") { setDragIndex(null); return; }
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); return; }
    const reordered = [...items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setDragIndex(null);
    persistOrder(reordered);
  };

  const visibleItems = items.filter((i) => (filter === "ALL" ? true : filter === "GENERAL" ? !i.store : i.store === filter));

  return (
    <div data-testid="store-manager">
      <div className="bg-white border border-line p-5 mb-8" data-testid="store-groups-manager">
        <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">SPIRIT STORES <span className="text-navy/40 normal-case tracking-normal">(each one is its own shop tab on the store page — HIDE keeps it private, products and all)</span></label>
        <div className="flex flex-wrap gap-2 mb-3">
          {groups.map((g) => (
            <span key={g.id} className={`flex items-center gap-1 border pl-3 pr-1.5 py-1.5 text-xs font-semibold ${g.active === false ? "border-red-200 bg-red-50 text-navy/50" : "border-line bg-paper text-navy/80"}`} data-testid={`store-group-chip-${g.id}`}>
              {g.name} <span className="text-navy/40 font-normal">({g.product_count ?? 0})</span>
              {g.active === false && <span className="text-[9px] font-bold tracking-wide text-red-700 bg-red-100 px-1 py-0.5">HIDDEN</span>}
              <button onClick={() => toggleGroup(g)} title={g.active === false ? "Open to public" : "Hide from public"} data-testid={`store-group-toggle-${g.id}`}
                className="ml-1 text-[10px] font-bold tracking-[0.08em] px-1.5 py-1 border border-line hover:border-navy text-navy/60 hover:text-navy transition-colors">
                {g.active === false ? "OPEN" : "HIDE"}
              </button>
              <button onClick={() => renameGroup(g)} title="Rename" data-testid={`store-group-rename-${g.id}`} className="p-1 text-navy/40 hover:text-navy"><Pencil size={12} /></button>
              <button onClick={() => deleteGroup(g)} title="Delete" data-testid={`store-group-delete-${g.id}`} className="p-1 text-navy/40 hover:text-red-600"><X size={12} /></button>
            </span>
          ))}
          {groups.length === 0 && <p className="text-navy/40 text-xs">No separate stores yet — everything shows on one page until you add store groups here.</p>}
        </div>
        <div className="flex gap-2 max-w-md">
          <input value={newGroup} onChange={(e) => setNewGroup(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addGroup()} data-testid="store-group-input"
            className="flex-1 border border-line px-4 py-2.5 text-sm outline-none focus:border-navy" placeholder="e.g. OMEGA" />
          <button onClick={addGroup} data-testid="store-group-add"
            className="bg-ink text-white px-4 flex items-center gap-1.5 text-xs font-bold tracking-[0.1em] hover:bg-navy transition-colors">
            <Plus size={14} /> ADD STORE
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-navy/60 text-sm tracking-[0.1em]">{visibleItems.length} PRODUCT{visibleItems.length === 1 ? "" : "S"}{filter !== "ALL" ? ` IN ${filter}` : ""}</p>
          {items.length > 1 && filter === "ALL" && <p className="text-navy/40 text-xs mt-1 flex items-center gap-1.5"><GripVertical size={12} /> Drag the handle to reorder</p>}
        </div>
        {!adding && editingId === null && (
          <button onClick={() => setAdding(true)} data-testid="add-store-button"
            className="flex items-center gap-2 bg-ink text-[#f2f0ea] px-5 py-2.5 text-xs font-bold tracking-[0.14em] hover:bg-navy transition-colors">
            <Plus size={15} /> ADD PRODUCT
          </button>
        )}
      </div>

      {groups.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="store-filter-pills">
          {["ALL", ...groups.map((g) => g.name), "GENERAL"].map((name) => (
            <button key={name} onClick={() => setFilter(name)} data-testid={`store-filter-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-4 py-2 text-[11px] font-bold tracking-[0.1em] border transition-colors ${filter === name ? "bg-ink text-[#f2f0ea] border-ink" : "border-line text-navy/60 hover:border-navy bg-white"}`}>
              {name.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {adding && <ProductForm initial={EMPTY} groups={groups} onSubmit={create} onCancel={() => setAdding(false)} submitting={submitting} />}

      {loading ? (
        <p className="text-navy/50">Loading…</p>
      ) : items.length === 0 && !adding ? (
        <div className="bg-white border border-line p-12 text-center text-navy/60">No products yet. Add your first one.</div>
      ) : (
        <div className="space-y-4" data-testid="store-list">
          {visibleItems.map((item, idx) => (
            <div key={item.id}>
              {editingId === item.id ? (
                <ProductForm
                  groups={groups}
                  initial={{ id: item.id, name: item.name, description: item.description || "", price: String(item.price), sizes: fromList(item.sizes), colors: fromList(item.colors), fulfillment: item.fulfillment, ship_fee: String(item.ship_fee || ""), track_inventory: !!item.track_inventory, stock: item.track_inventory ? String(item.stock ?? "") : "", personalization: !!item.personalization, personalization_fee: item.personalization ? String(item.personalization_fee ?? "5") : "5", personalization_label: item.personalization_label || "Name / text to print", images: item.images?.length ? item.images : (item.img ? [item.img] : []), image_labels: item.image_labels || [], store: item.store || "", size_upcharges: item.size_upcharges || {} }}
                  onSubmit={(f) => update(item.id, f)}
                  onCancel={() => setEditingId(null)}
                  submitting={submitting}
                />
              ) : (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(idx)}
                  className={`bg-white border p-4 flex gap-4 items-center transition-all ${dragIndex === idx ? "opacity-40 border-gold" : "border-line"} ${!item.active ? "opacity-60" : ""}`}
                  data-testid={`store-row-${item.id}`}>
                  {filter === "ALL" && (
                    <div draggable onDragStart={() => setDragIndex(idx)} onDragEnd={() => setDragIndex(null)}
                      className="text-navy/30 hover:text-navy cursor-grab active:cursor-grabbing shrink-0" title="Drag to reorder">
                      <GripVertical size={18} />
                    </div>
                  )}
                  <div className="w-16 h-16 bg-[#e9e6dd] shrink-0 overflow-hidden">
                    <img src={resolveImg(item.img)} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      {item.store && <span className="bg-gold/25 text-ink text-[9px] font-bold tracking-[0.1em] px-1.5 py-0.5 shrink-0" data-testid={`store-badge-${item.id}`}>{item.store.toUpperCase()}</span>}
                      <p className="font-semibold text-sm text-ink truncate">{item.name}</p>
                    </div>
                    <p className="text-navy/60 text-xs mt-0.5">
                      ${Number(item.price).toFixed(2)} · {item.fulfillment === "ship" ? `Ships ($${Number(item.ship_fee || 0).toFixed(2)})` : "Pickup"}
                      {item.sizes?.length ? ` · ${item.sizes.length} sizes` : ""}{item.colors?.length ? ` · ${item.colors.length} colors` : ""}
                      {item.track_inventory ? ` · ${item.stock ?? 0} in stock` : ""}
                      {item.personalization ? ` · personalize +$${Number(item.personalization_fee || 0).toFixed(2)}` : ""}
                      {Object.keys(item.size_upcharges || {}).length ? " · size extras" : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => toggleActive(item)} data-testid={`toggle-active-${item.id}`} title={item.active ? "Hide from store" : "Show in store"}
                      className="w-9 h-9 flex items-center justify-center border border-line text-navy/60 hover:text-ink hover:border-navy transition-colors">
                      {item.active ? <Eye size={15} /> : <EyeOff size={15} />}
                    </button>
                    <button onClick={() => { setEditingId(item.id); setAdding(false); }} data-testid={`edit-store-${item.id}`} title="Edit"
                      className="w-9 h-9 flex items-center justify-center border border-line text-navy/60 hover:text-gold hover:border-navy transition-colors">
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => remove(item.id)} data-testid={`delete-store-${item.id}`} title="Delete"
                      className="w-9 h-9 flex items-center justify-center border border-line text-red-600 hover:bg-red-600 hover:text-white transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
